import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { couleurSalarie } from './prestationPlanningShared';

export interface SalarieFiltrable {
    id: string;
    first_name: string;
    last_name: string;
    job_post_title?: string;
    couleur_planning?: string | null;
}

interface Props {
    employees: SalarieFiltrable[];
    // Identifiants cochés. Liste vide = tous les salariés sont affichés.
    selection: string[];
    onChange: (ids: string[]) => void;
}

const nomComplet = (e: SalarieFiltrable) => `${e.first_name} ${e.last_name}`.trim();
const MAX_ETIQUETTES = 4;

// Filtre du planning par salarié (maquette validée 2026-11-01) : un champ de recherche et une liste à cocher plutôt
// qu'une pastille par salarié, pour rester lisible avec 70 salariés ou plus.
export const FiltreSalaries: React.FC<Props> = ({ employees, selection, onChange }) => {
    const [ouvert, setOuvert] = useState(false);
    const [recherche, setRecherche] = useState('');
    const racine = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ouvert) return;
        const fermerAuClicExterieur = (ev: MouseEvent) => {
            if (racine.current && !racine.current.contains(ev.target as Node)) setOuvert(false);
        };
        document.addEventListener('mousedown', fermerAuClicExterieur);
        return () => document.removeEventListener('mousedown', fermerAuClicExterieur);
    }, [ouvert]);

    const trouves = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        const liste = [...employees].sort((a, b) => nomComplet(a).localeCompare(nomComplet(b), 'fr'));
        if (!q) return liste;
        return liste.filter((e) => nomComplet(e).toLowerCase().includes(q) || String(e.job_post_title || '').toLowerCase().includes(q));
    }, [employees, recherche]);

    const bascule = (id: string) => onChange(selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id]);
    const choisis = selection.map((id) => employees.find((e) => e.id === id)).filter((e): e is SalarieFiltrable => !!e);

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <div ref={racine} className="relative w-full sm:w-72">
                <div className="flex items-center gap-2 bg-surface-soft border border-border rounded-xl px-3 focus-within:border-brand-500 focus-within:bg-surface transition-colors">
                    <Search size={14} className="text-content-muted shrink-0" aria-hidden />
                    <input
                        type="text"
                        value={recherche}
                        onChange={(e) => { setRecherche(e.target.value); setOuvert(true); }}
                        onFocus={() => setOuvert(true)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setOuvert(false); (e.target as HTMLInputElement).blur(); } }}
                        placeholder="Rechercher un salarié…"
                        aria-label="Rechercher un salarié"
                        aria-expanded={ouvert}
                        aria-controls="filtre-salaries-liste"
                        role="combobox"
                        autoComplete="off"
                        className="flex-1 min-w-0 bg-transparent py-2.5 text-sm text-content outline-none"
                    />
                </div>
                {ouvert && (
                    <div className="absolute left-0 top-full mt-1.5 w-full sm:w-[22rem] max-w-[90vw] bg-surface border border-border rounded-xl shadow-2xl z-40 overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-border text-[11px] text-content-muted">
                            <span>{trouves.length} salarié{trouves.length > 1 ? 's' : ''}{recherche.trim() ? ` trouvé${trouves.length > 1 ? 's' : ''}` : ''}</span>
                            {selection.length > 0 && (
                                <button type="button" onClick={() => onChange([])} className="font-bold text-brand-600 hover:underline">Tout décocher</button>
                            )}
                        </div>
                        <div id="filtre-salaries-liste" className="max-h-64 overflow-y-auto p-1">
                            {trouves.length === 0 ? (
                                <p className="px-3 py-4 text-xs text-content-muted text-center">Aucun salarié ne correspond à « {recherche} »</p>
                            ) : trouves.map((e) => (
                                <label key={e.id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer hover:bg-surface-soft text-sm text-content">
                                    <input type="checkbox" className="accent-brand-600" checked={selection.includes(e.id)} onChange={() => bascule(e.id)} />
                                    <span aria-hidden className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: couleurSalarie(e) }} />
                                    <span className="font-semibold flex-1 min-w-0 truncate">{nomComplet(e)}</span>
                                    {e.job_post_title && <span className="text-[11px] text-content-muted truncate max-w-[7rem]">{e.job_post_title}</span>}
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {choisis.length === 0 ? (
                <span className="text-xs text-content-muted">Tous les salariés ({employees.length})</span>
            ) : (
                <>
                    {choisis.slice(0, MAX_ETIQUETTES).map((e) => {
                        const c = couleurSalarie(e);
                        return (
                            <span key={e.id} className="inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-1 text-xs font-semibold text-content"
                                style={{ borderColor: c, backgroundColor: `color-mix(in srgb, ${c} 12%, var(--bg-card))` }}>
                                <span aria-hidden className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
                                {nomComplet(e)}
                                <button type="button" onClick={() => bascule(e.id)} aria-label={`Retirer ${nomComplet(e)} du filtre`}
                                    className="p-0.5 rounded-full text-content-muted hover:text-content"><X size={12} /></button>
                            </span>
                        );
                    })}
                    {choisis.length > MAX_ETIQUETTES && (
                        <span className="rounded-full border border-border bg-surface-soft px-2.5 py-1 text-xs font-semibold text-content-muted">+{choisis.length - MAX_ETIQUETTES} autres</span>
                    )}
                    <button type="button" onClick={() => onChange([])} className="text-xs font-bold text-brand-600 hover:underline">Tout effacer</button>
                </>
            )}
        </div>
    );
};
